import { EFinancingProgram } from '@if/core/types'
import { ActionList, useCan, useIfUser, useSnackbar, type Action, type OverflowAction, type Tone } from '@if/mui'
import { access } from '@src/access'
import { useGenerateValidationReport, useReplayMigrationEvents } from '@src/api/loan/loan-api'
import { useLoanLock } from '@src/providers/LoanLockProvider'
import { useMonolithBaseUrl } from '@src/services/monolith-hook'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import {
  Ban,
  CalendarDays,
  ChartColumn,
  Contact,
  Database,
  FileText,
  FolderOpen,
  HandCoins,
  History,
  Images,
  Landmark,
  Lock,
  LockOpen,
  Megaphone,
  Merge,
  Printer,
  RotateCcw,
  Settings,
  ShieldCheck,
  StickyNote,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoanCalendarCurrentLoanStatus } from './LoanCalendar-hooks'
import { useLoanCalendarLoanStatusHistoryDialog } from './LoanCalendarLoanStatusHistory/LoanCalendarLoanStatusHistoryDialog-hook'
import ReplayMigrationEventsCountdown, { REPLAY_SNACKBAR_DURATION } from './ReplayMigrationEventsCountdown'

const noop = () => {}

interface LoanCalendarActionsProps {
  simulation: CalendarSimulation
}

const LoanCalendarActions = ({ simulation }: LoanCalendarActionsProps) => {
  const { t } = useTranslation()
  const { id: loanId, financingCompanyId } = simulation.fullLoan.loan
  const currentLoanStatus = useLoanCalendarCurrentLoanStatus(simulation)
  const loanStatusHistoryDialog = useLoanCalendarLoanStatusHistoryDialog()
  const user = useIfUser()
  const { showSnackbar, showSuccess, showError } = useSnackbar()
  const { lock, isLocked, isLockedByOther, isLockedBySystem, isLoading, acquireLock, releaseLock } = useLoanLock()

  const canForceUnlock = useCan(access.forceUnlockLoan)
  const canReplayMigrationEvents = useCan(access.replayLoanMigrationEvents)
  const canOpenMonolithCalendar = useCan(access.openMonolithCalendar)
  const canGenerateValidationReport = useCan(access.generateValidationReport)

  const monolithBaseUrl = useMonolithBaseUrl()
  const [isReplayPending, setIsReplayPending] = useState(false)
  const generateReportMutation = useGenerateValidationReport(loanId, financingCompanyId)
  const replayMigrationEventsMutation = useReplayMigrationEvents(loanId, financingCompanyId)

  const handleGenerateReportClick = () => {
    if (generateReportMutation.isPending) return

    generateReportMutation.mutate(undefined, {
      onSuccess: () => showSuccess(t('loan.payment.generateReport.success', { email: user.email })),
      onError: () => showError(t('loan.payment.generateReport.error')),
    })
  }

  const replayMigrationEvents = () => {
    replayMigrationEventsMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess(t('loan.payment.replayMigrationEvents.success'))
        setIsReplayPending(false)
      },
      onError: () => {
        showError(t('loan.payment.replayMigrationEvents.error'))
        setIsReplayPending(false)
      },
    })
  }

  const handleReplayMigrationEventsClick = () => {
    if (isReplayPending) return

    setIsReplayPending(true)
    showSnackbar(
      <ReplayMigrationEventsCountdown onElapsed={replayMigrationEvents} onCancel={() => setIsReplayPending(false)} />,
      { severity: 'warning', autoHideDuration: REPLAY_SNACKBAR_DURATION },
    )
  }

  const handleLockClick = () => {
    if (isLockedBySystem) return
    if (isLocked) {
      void releaseLock()
    } else if (isLockedByOther) {
      if (canForceUnlock) {
        void releaseLock()
      } else {
        const expiresOn = lock?.expiresOn ? new Date(lock.expiresOn).toLocaleString() : 'unknown'
        showError(
          t('common.lockError.failedRelease', {
            username: lock?.username || 'Unknown user',
            expiresOn,
          }),
        )
      }
    } else {
      void acquireLock()
    }
  }

  const subscriptionFileUrl = useMemo(() => {
    let sfUrl = ''
    const appId = simulation.fullLoan.loan.creditApplicationId

    if (appId != null) {
      switch (simulation.fullLoan.loan.financingProgramId) {
        case EFinancingProgram.IfXpress:
          sfUrl = `${monolithBaseUrl}/secure/ifp/demande-resume/${appId}`
          break
        case EFinancingProgram.Auto:
        case EFinancingProgram.Products:
        case EFinancingProgram.Personal:
        case EFinancingProgram.B2c:
        case EFinancingProgram.AutoRsla:
        case EFinancingProgram.MechanicalWarranty:
        case EFinancingProgram.CreditRsla:
        default:
      }
    }
    return sfUrl
  }, [simulation.fullLoan.loan.financingProgramId, simulation.fullLoan.loan.creditApplicationId, monolithBaseUrl])

  const isSubscriptionFileDisabled = subscriptionFileUrl === ''

  const handleSubscriptionFileClick = () => {
    if (subscriptionFileUrl !== '') {
      window.open(subscriptionFileUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const getLockTooltip = () => {
    if (isLockedBySystem) {
      return t('common.lockTooltip.lockedBySystem')
    }
    if (isLockedByOther) {
      return (
        <>
          {t('common.lockTooltip.lockedByOther')}
          {canForceUnlock && (
            <>
              <br />
              {t('common.lockTooltip.lockedByOtherAdmin')}
            </>
          )}
        </>
      )
    }
    if (isLocked) {
      return t('common.lockTooltip.locked')
    }
    return t('common.lockTooltip.unlocked')
  }

  const getLockTone = (): Tone => {
    if (isLockedBySystem) return 'destructive'
    if (isLockedByOther) return canForceUnlock ? 'warning' : 'destructive'
    if (isLocked) return 'success'
    return 'default'
  }

  const getLockIcon = () => {
    if (isLockedBySystem) return Ban
    if (isLocked || isLockedByOther) return Lock
    return LockOpen
  }

  const actions: Action[] = [
    {
      icon: History,
      label: t('loan.payment.loanStatusHistory'),
      onClick: () =>
        void loanStatusHistoryDialog.open({
          loanStatusHistory: simulation.fullLoan.loanStatusHistory,
          currentLoanStatus,
        }),
    },
    {
      icon: getLockIcon(),
      label: t('common.lockAction'),
      tooltip: getLockTooltip(),
      tone: getLockTone(),
      disabled: isLoading,
      onClick: handleLockClick,
    },
    { icon: Megaphone, label: t('loan.payment.noAlert'), disabled: true, onClick: noop },
    { icon: Printer, label: t('loan.payment.print'), onClick: noop },
    { icon: ShieldCheck, label: t('loan.payment.noAssurance'), disabled: true, onClick: noop },
    { icon: HandCoins, label: t('loan.payment.noCollection'), disabled: true, onClick: noop },
    {
      icon: FolderOpen,
      label: t('loan.payment.subscriptionFile'),
      disabled: isSubscriptionFileDisabled,
      onClick: handleSubscriptionFileClick,
    },
    ...(canReplayMigrationEvents
      ? [
          {
            icon: RotateCcw,
            label: t('loan.payment.replayMigrationEvents.tooltip'),
            disabled: isReplayPending,
            onClick: handleReplayMigrationEventsClick,
          } satisfies Action,
        ]
      : []),
    ...(canOpenMonolithCalendar
      ? [
          {
            icon: CalendarDays,
            label: t('loan.payment.openMonolithCalendar'),
            href: `${monolithBaseUrl}/admin/customer/${simulation.fullLoan.loan.originalLoanId}`,
            target: '_blank',
          } satisfies Action,
        ]
      : []),
    ...(canGenerateValidationReport
      ? [
          {
            icon: FileText,
            label: t('loan.payment.generateReport.tooltip'),
            disabled: generateReportMutation.isPending,
            onClick: handleGenerateReportClick,
          } satisfies Action,
        ]
      : []),
  ]

  const fileMenuActions: OverflowAction[] = [
    { icon: Merge, label: t('loan.payment.fileMenu.fusion'), onClick: noop },
    { icon: Contact, label: t('loan.payment.fileMenu.info'), onClick: noop },
    { icon: Database, label: t('loan.payment.fileMenu.data'), onClick: noop },
    { icon: StickyNote, label: t('loan.payment.fileMenu.notes'), onClick: noop },
    { icon: Images, label: t('loan.payment.fileMenu.files'), onClick: noop },
    { icon: Settings, label: t('loan.payment.fileMenu.configPPA'), onClick: noop },
    { icon: History, label: t('loan.payment.fileMenu.history'), onClick: noop },
    { icon: Landmark, label: t('loan.payment.fileMenu.metro2'), disabled: true, onClick: noop },
    { icon: ChartColumn, label: t('loan.payment.fileMenu.transactions'), onClick: noop },
  ]

  return (
    <ActionList
      actions={actions}
      overflowActions={fileMenuActions}
      variant="icon"
      overflowLabel={t('loan.payment.fileMenu.tooltip')}
      menuLabel={t('loan.payment.otherGlobalActions')}
    />
  )
}

export default LoanCalendarActions
